'use client';

import { useCsamCredentials, useReactiveFeatureFlagValue } from '@/app/contexts';
import type { CsamCredentials } from '@/lib/csam-credentials';

export type CsamAccessLevel = 'none' | 'tenant-default' | 'limited-user' | 'verifier';

export type CsamAccess = {
  level: CsamAccessLevel;
  canFlagMedia: boolean;
  canSeeCsamTags: boolean;
  canSeeDescriptions: boolean;
  canOpenDashboard: boolean;
  canRevealOriginal: boolean;
  canVerifyFindings: boolean;
  canOpenAuditLog: boolean;
  ready: boolean;
};

export type CsamAccessInput = CsamCredentials & {
  tenantAccess: boolean;
  logAccess: boolean;
  ready: boolean;
};

const CLOSED: Omit<CsamAccess, 'ready'> = {
  level: 'none',
  canFlagMedia: false,
  canSeeCsamTags: false,
  canSeeDescriptions: false,
  canOpenDashboard: false,
  canRevealOriginal: false,
  canVerifyFindings: false,
  canOpenAuditLog: false,
};

/**
 * Pure CSAM capability matrix. Call sites must read named capabilities, not
 * compare levels. Tenant flag OFF collapses to no CSAM UI regardless of role.
 */
export function resolveCsamAccess(input: CsamAccessInput): CsamAccess {
  const { tenantAccess, logAccess, ready } = input;
  if (!tenantAccess) {
    return { ...CLOSED, ready };
  }

  const verifier = input.csamVerifier;
  const limited = input.csamLimitedUser || verifier;

  let level: CsamAccessLevel = 'tenant-default';
  if (verifier) level = 'verifier';
  else if (limited) level = 'limited-user';

  return {
    level,
    canFlagMedia: true,
    canSeeCsamTags: true,
    canSeeDescriptions: limited,
    canOpenDashboard: limited,
    canRevealOriginal: verifier,
    canVerifyFindings: verifier,
    canOpenAuditLog: limited && logAccess,
    ready,
  };
}

export function useCsamAccess(): CsamAccess {
  const { csamLimitedUser, csamVerifier } = useCsamCredentials();
  const tenantAccess = Boolean(useReactiveFeatureFlagValue('csamTenantAccess'));
  const logAccess = Boolean(useReactiveFeatureFlagValue('csamLogAccess'));
  return resolveCsamAccess({
    csamLimitedUser,
    csamVerifier,
    tenantAccess,
    logAccess,
    ready: true,
  });
}
